import { useContext, useRef, useState } from "react";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { partition, sortBy } from "lodash";
import {
  canDeleteSavedView,
  canUpdateSavedView,
} from "common/authorization/customPolicies";
import { FilterDescription } from "common/utils/queryBuilder";
import { RoleName } from "../../../../../generated/graphql";
import { styled } from "../../../../../stitches.config";
import { AuthContext } from "../../../../Authorization/AuthContext";
import { Button } from "../../../Button";
import Divider from "../../../Divider";
import { Icon } from "../../../Icons/LucideIcons";
import { Body } from "../../../Typography";
import { TableContext } from "../../TableContext";
import { useUpdateSavedView } from "../../utils";
import {
  AttributeLabel,
  DropdownContentStyles,
  DropdownItemStyles,
} from "../__styles__/TableSettings";
import { AttributeRow, SavedView } from "../utils";
import { SavedViewItemIcon, SavedViewItemRow } from "./__styles__/SavedViews";
import { useDeleteSavedViewModal } from "./DeleteModal";
import { useUpsertSavedViewModal } from "./UpsertSavedViewModal";

export const SavedViewsButton = ({
  savedViews,
  onAddNewView,
  addingNewView,
}: {
  savedViews?: Array<SavedView>;
  addingNewView: boolean;
  onAddNewView: () => void;
  additionalFilters?: Array<FilterDescription>;
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { currentView } = useContext(TableContext);
  const showDeleteSavedViewModal = useDeleteSavedViewModal();
  const [showUpsertModal] = useUpsertSavedViewModal({});

  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(prevOpen => !prevOpen);
  };

  const handleReturnFocusToButton = (event: Event) => {
    event.preventDefault();
    buttonRef?.current?.focus();
  };

  if (!currentView) {
    return null;
  }

  const [internalViews, publicViews] = partition(
    sortBy(savedViews, "name"),
    view => view.hiddenFromPublic,
  );

  return (
    <div style={{ userSelect: "none" }}>
      <Button
        ref={buttonRef}
        onClick={toggleMenu}
        rightIconName="chevron-down"
        leftIconName={currentView.hiddenFromPublic ? undefined : "users-round"}
        styleVariant="secondary"
        size="small"
        disabled={addingNewView}
      >
        {addingNewView ? "Adding view" : currentView.name}
      </Button>
      <Dropdown.Root open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <Dropdown.Trigger asChild>
          <div />
        </Dropdown.Trigger>
        <DropdownContent
          align="start"
          sideOffset={4}
          loop
          onCloseAutoFocus={handleReturnFocusToButton}
        >
          <DropdownItem onSelect={onAddNewView}>
            <AttributeRow attribute={{ label: "Add new view", icon: "plus" }} />
          </DropdownItem>
          <Divider extension={8} />
          {!!internalViews.length && (
            <Dropdown.Sub>
              <SavedViewItemRow>
                <DropdownItem
                  key="internal-views"
                  disabled={true}
                  omitHoverStyles={true}
                  onSelect={() => {}}
                >
                  <Body size="small" type="emphasis" color="contentPlaceholder">
                    INTERNAL VIEWS
                  </Body>
                </DropdownItem>
              </SavedViewItemRow>
            </Dropdown.Sub>
          )}
          {internalViews.map(savedView => (
            <SavedViewItem
              savedView={savedView}
              key={savedView.id}
              onDelete={() => showDeleteSavedViewModal(savedView)}
              onEditView={() => showUpsertModal(savedView)}
            />
          ))}
          {!!publicViews.length && (
            <Dropdown.Sub>
              <SavedViewItemRow>
                <DropdownItem
                  key="public-views"
                  disabled={true}
                  omitHoverStyles={true}
                  onSelect={() => {}}
                >
                  <Body size="small" type="emphasis" color="contentPlaceholder">
                    PUBLIC VIEWS
                  </Body>
                </DropdownItem>
              </SavedViewItemRow>
            </Dropdown.Sub>
          )}
          {publicViews.map(savedView => (
            <SavedViewItem
              savedView={savedView}
              key={savedView.id}
              onDelete={() => showDeleteSavedViewModal(savedView)}
              onEditView={() => showUpsertModal(savedView)}
            />
          ))}
        </DropdownContent>
      </Dropdown.Root>
    </div>
  );
};

const SavedViewItem = ({
  savedView,
  onDelete,
  onEditView,
}: {
  savedView: SavedView;
  onDelete: () => void;
  onEditView: () => void;
}) => {
  const { user, admin } = useContext(AuthContext);
  const { currentView, defaultView } = useContext(TableContext);
  const { updateSavedView } = useUpdateSavedView();

  const disabled = savedView.id === currentView!.id;

  const isDefaultView = savedView.id === defaultView?.id;
  const userEntity = user || admin;

  const canDelete =
    canDeleteSavedView({
      user: { role: userEntity?.role.name as RoleName, id: userEntity?.id! },
      createdById: savedView.createdById ?? null,
    }) && !isDefaultView;

  const canUpdate = canUpdateSavedView({
    user: { role: userEntity?.role.name as RoleName, id: userEntity?.id! },
    createdById: savedView.createdById,
  });

  return (
    <>
      <Dropdown.Sub>
        <SavedViewItemRow>
          <DropdownItem
            key={savedView.id}
            disabled={disabled}
            omitHoverStyles={disabled}
            onSelect={() => {
              updateSavedView(savedView.id);
            }}
          >
            <AttributeLabel disabled={disabled}>
              {savedView.name}
            </AttributeLabel>
          </DropdownItem>
          <Dropdown.SubTrigger asChild>
            <SavedViewItemIcon data-testid="saved-view-overflow">
              <Icon
                iconName={"ellipsis-vertical"}
                color="contentSecondary"
                size={16}
                style={{
                  cursor: "pointer",
                }}
              />
            </SavedViewItemIcon>
          </Dropdown.SubTrigger>
        </SavedViewItemRow>
        <SubDropdownContent>
          <DropdownItem
            onSelect={() => {
              updateSavedView(savedView.id);
              onEditView();
            }}
            disabled={!canUpdate}
          >
            <AttributeLabel disabled={!canUpdate}>Edit view</AttributeLabel>
          </DropdownItem>
          <DropdownItem onSelect={onDelete} disabled={!canDelete}>
            <AttributeLabel disabled={!canDelete} red={canDelete}>
              Delete
            </AttributeLabel>
          </DropdownItem>
        </SubDropdownContent>
      </Dropdown.Sub>
    </>
  );
};

const DropdownContent = styled(Dropdown.Content, {
  ...DropdownContentStyles,
  maxHeight: "calc(100vh - 300px)",
  overflowY: "auto",
  /**
   * Prevents horizontal scrollbars on Safari
   */
  overflowX: "clip",
});

const SubDropdownContent = styled(Dropdown.SubContent, {
  ...DropdownContentStyles,
  width: "max-content",
});

const DropdownItem = styled(Dropdown.Item, {
  ...DropdownItemStyles,
});
